/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { FadeIn } from "@/components/landing/primitives/fade-in";
import MainNavigation from "@/components/layout/main-navigation";
import { EMAILS } from "@/lib/constants/master";
import {
  ArrowRight,
  CheckCircle,
  Cloud,
  FileSpreadsheet,
  Receipt,
  RefreshCw,
  Shield,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const integrations = [
  {
    name: "QuickBooks Online",
    description:
      "Sync estimates, invoices, and client data with QuickBooks for seamless accounting.",
    icon: Receipt,
    status: "in-progress" as const,
    features: ["Budget import", "Chart of accounts sync", "Client data import"],
  },
  {
    name: "Excel & CSV Import",
    description:
      "Import your existing cost databases, estimates, and client lists from spreadsheets.",
    icon: FileSpreadsheet,
    status: "available" as const,
    features: [
      "Bulk data import",
      "Cost database migration",
      "Template mapping",
    ],
  },
  {
    name: "Xero",
    description:
      "Connect with Xero for automated invoicing and financial reconciliation.",
    icon: Cloud,
    status: "planned" as const,
    features: ["Invoice sync", "Payment tracking", "Financial reports"],
  },
  {
    name: "Supplier Catalogs",
    description:
      "Real-time pricing from your preferred suppliers automatically updated in estimates.",
    icon: RefreshCw,
    status: "planned" as const,
    features: ["Live pricing", "Auto-update costs", "Multi-supplier support"],
  },
];

const statusLabels = {
  available: { text: "Available", className: "bg-success/10 text-success" },
  "in-progress": {
    text: "Coming Soon",
    className: "bg-secondary/10 text-secondary",
  },
  planned: { text: "Planned", className: "bg-muted text-muted-foreground" },
};

const benefits = [
  {
    icon: Zap,
    title: "Save Hours Weekly",
    description:
      "Eliminate manual data entry between your estimating and accounting software.",
  },
  {
    icon: RefreshCw,
    title: "Import Once, Use Everywhere",
    description:
      "Bring your existing data into CC and use it across all your projects. No more re-entering the same information.",
  },
  {
    icon: Shield,
    title: "Secure Connections",
    description:
      "All integrations use encrypted, OAuth-authenticated connections. Your data stays safe.",
  },
];

export function IntegrationsMarketingClient() {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <MarketingPageHero
        variant="dark"
        badge="Integrations"
        title={
          <>
            Connect your{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              existing tools
            </span>
          </>
        }
        description="Contractor Co-Pilot integrates with the tools you already use. Import data, sync finances, and keep everything connected."
      />

      {/* Integration Cards */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background"
        aria-labelledby="integrations-list"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="Ecosystem"
            title="Built to connect"
            description="Import your data, sync your finances, and connect your workflow."
          />

          <div className="grid md:grid-cols-2 gap-6">
            {integrations.map((integration, index) => {
              const Icon = integration.icon;
              const status = statusLabels[integration.status];

              return (
                <FadeIn
                  key={integration.name}
                  as="article"
                  delay={index * 100}
                  className="card-premium group bg-muted/30 rounded-2xl p-6 border border-border"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary transition-transform duration-300 group-hover:scale-110"
                      aria-hidden="true"
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${status.className}`}
                    >
                      {status.text}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-foreground mb-2 font-display">
                    {integration.name}
                  </h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {integration.description}
                  </p>

                  <ul className="space-y-2" role="list">
                    {integration.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm"
                      >
                        <CheckCircle
                          className="w-4 h-4 text-secondary flex-shrink-0"
                          aria-hidden="true"
                        />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background-warm"
        aria-labelledby="integration-benefits"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="Why Integrate"
            title="Stop copying data between tools"
            description="Integrations save you time and eliminate errors from manual data entry."
          />

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <FadeIn
                  key={benefit.title}
                  as="article"
                  delay={index * 100}
                  className="card-premium bg-background p-6 rounded-2xl border border-border"
                >
                  <div
                    className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary"
                    aria-hidden="true"
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-foreground font-display">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Request Integration */}
      <section
        className="py-16 md:py-24 bg-background"
        aria-labelledby="request-integration"
      >
        <FadeIn className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16 text-center">
          <h2 className="text-lg md:text-xl font-bold text-foreground mb-3 font-display">
            Need a different integration?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            We&apos;re building integrations based on contractor feedback. Tell
            us what tools you use and we&apos;ll prioritize accordingly.
          </p>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-border hover:border-secondary/20"
          >
            <a href={`mailto:${EMAILS.CONTACT}?subject=Integration Request`}>
              Request an Integration
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </FadeIn>
      </section>

      <MarketingCTASection
        title="Ready to streamline your workflow?"
        description="Join contractors who are saving hours every week with connected tools."
        primaryCTA={{ text: "Start Your Free Trial", href: "/auth/sign-up" }}
        secondaryCTA={{ text: "Contact Us", href: `mailto:${EMAILS.CONTACT}` }}
        trustIndicators={["No setup fees • Cancel anytime"]}
      />

      <Footer />
    </div>
  );
}
